import { Button, Card, CardContent, Label, Textarea } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Loader2, Sparkles } from "@/components/icons";
import { SimpleTemplateGrid } from "@/components/setup/simple-template-grid";
import { StepFooter, StepHeading } from "@/components/setup/step-chrome";
import type { SetupStepProps } from "@/components/setup/steps";
import { ThemeCardPreview } from "@/components/setup/theme-card-preview";
import { generateMyTheme, updateMySiteConfig } from "@/lib/api";
import type { Palette } from "@/lib/color-scheme";
import { toAppError } from "@/lib/errors";
import { CUSTOM_TEMPLATE_ID } from "@/lib/templates";
import type { FontScheme, Template } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Wizard step 2 — the page's look: pick a ready-made template with one
 * tap, or describe a vibe and let AI design a matching palette + font
 * pairing. Fully skippable; the page already carries the default look.
 */
export function StepLook({
  orderId,
  setup,
  linear,
  onDone,
  onBack,
}: SetupStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { site, config, templates } = setup;
  const siteId = site?.siteId ?? "";
  const sampleHeading = site?.displayName?.trim() || t("setup.yourNames");

  const activeId = (config?.templateId) ?? null;
  const [pendingId, setPendingId] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["mySetup", orderId] });

  const applyTemplate = useMutation({
    mutationFn: (template: Template) =>
      updateMySiteConfig({
        siteId,
        configPatch: {
          colorScheme: {
            seeds: { seed: template.palette.primary },
            palette: template.palette,
          },
          fontScheme: template.fontScheme,
          templateId: template.id,
        },
      }),
    onSuccess: async (_res, template) => {
      toast.success(t("setup.look.applied", { name: template.name }));
      await invalidate();
      setPendingId(null);
    },
    onError: (err) => {
      toast.error(toAppError(err).message);
      setPendingId(null);
    },
  });

  // AI theming: generate → preview → explicit "Use this look".
  const [aiMessage, setAiMessage] = useState("");
  const [palette, setPalette] = useState<Palette | null>(null);
  const [fontScheme, setFontScheme] = useState<FontScheme | null>(null);
  const [seed, setSeed] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasError, setHasError] = useState(false);

  const aiMutation = useMutation({
    mutationFn: (message: string) => generateMyTheme({ siteId, message }),
    onSuccess: (resp) => {
      setPalette(resp.palette);
      setFontScheme(resp.fontScheme);
      setSeed(resp.seed);
      setWarnings(resp.warnings ?? []);
    },
    onError: (err) => {
      setHasError(true);
      toast.error(toAppError(err).message);
    },
  });

  const applyAi = useMutation({
    mutationFn: () => {
      if (!palette || !fontScheme || !seed) {
        throw new Error(t("setup.look.generateFirst"));
      }
      return updateMySiteConfig({
        siteId,
        configPatch: {
          colorScheme: { seeds: { seed }, palette },
          fontScheme,
          templateId: CUSTOM_TEMPLATE_ID,
        },
      });
    },
    onSuccess: async () => {
      toast.success(t("setup.look.aiLive"));
      await invalidate();
      setPalette(null);
      setFontScheme(null);
      setSeed(null);
      setWarnings([]);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  // Single active preview stylesheet — only one AI result is shown at a time.
  useEffect(() => {
    const cssUrl = fontScheme?.cssUrl;
    if (!cssUrl) return;
    const id = "setup-ai-preview-font";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== cssUrl) link.href = cssUrl;
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [fontScheme?.cssUrl]);

  const busy = applyTemplate.isPending || aiMutation.isPending || applyAi.isPending;

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <StepHeading step="look" />

        <SimpleTemplateGrid
          templates={templates}
          activeId={activeId}
          pendingId={applyTemplate.isPending ? pendingId : null}
          disabled={busy}
          sampleName={site?.displayName}
          onPick={(template) => {
            setPendingId(template.id);
            applyTemplate.mutate(template);
          }}
        />

        <div className="space-y-3 rounded-lg bg-secondary p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <Label htmlFor="setup-ai" className="text-base">
              {t("setup.look.describeVibe")}
            </Label>
          </div>
          <div className="relative">
            {aiMutation.isPending && (
              <div
                className="pointer-events-none absolute inset-0 rounded-md"
                style={{
                  padding: 2,
                  background:
                    "conic-gradient(from var(--setup-ai-glow-angle), transparent, hsl(var(--primary)), transparent)",
                  WebkitMask:
                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  animation: "setup-ai-glow-spin 2.2s linear infinite",
                }}
              />
            )}
            <Textarea
              id="setup-ai"
              rows={2}
              placeholder={t("colorEditor.aiPlaceholder")}
              value={aiMessage}
              onChange={(e) => {
                setAiMessage(e.target.value);
                setHasError(false);
              }}
              className="min-h-[60px] resize-none rounded-md border-0 bg-card pr-12 shadow-sm outline-none focus-visible:outline-none focus-visible:ring-0"
            />
            <Button
              type="button"
              size="icon"
              aria-label={t("setup.look.designWithAi")}
              className={cn(
                "absolute bottom-2 right-2 rounded-full",
                hasError && "ring-2 ring-destructive ring-offset-2 ring-offset-card",
              )}
              disabled={busy || !aiMessage.trim()}
              onClick={() => aiMutation.mutate(aiMessage.trim())}
            >
              {aiMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {palette && fontScheme && (
          <div className="space-y-3">
            <ThemeCardPreview
              palette={palette}
              fontScheme={fontScheme}
              heading={sampleHeading}
              label={t("setup.look.aiLookLabel")}
              loading={aiMutation.isPending}
              className="border"
            />
            {warnings.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t("setup.look.contrastNote")}
              </p>
            )}
            <Button
              type="button"
              className="w-full"
              disabled={applyAi.isPending}
              onClick={() => applyAi.mutate()}
            >
              {applyAi.isPending ? t("setup.look.applying") : t("setup.look.useThisLook")}
            </Button>
          </div>
        )}

        <StepFooter onBack={onBack}>
          <Button type="button" size="lg" onClick={onDone} disabled={busy}>
            {linear ? t("setup.look.looksGood") : t("setup.look.done")}
          </Button>
        </StepFooter>
      </CardContent>
    </Card>
  );
}
